import { memo } from 'react'

import { Box, Center, Group, Stack, Text } from '@mantine/core'

import { IconArrowDown } from '@repo/common/components/Icons'
import { type VaultSymbol } from '@repo/common/queries/products'
import { useRedeemVault } from '@repo/common/queries/vaults'

import { ContentBox } from '@/components/ContentBox'
import {
  type BasePanelProps,
  classes,
  FlowButton,
  normalize_progress,
  NumberRow,
  RatioAndDisclosures,
  type StepsType,
  useFlowControl,
} from '@/flows/flow_helpers'

import { useVaultRedeemFields } from './useVaultRedeemFields'

type RedeemPanelProps = BasePanelProps

const steps: StepsType<RedeemPanelProps> = normalize_progress([])

export const VaultRedeem = memo<{
  symbol: VaultSymbol
  onCompleted: () => void
  // eslint-disable-next-line max-lines-per-function
}>(function VaultRedeem({ symbol, onCompleted }) {
  const {
    form,
    fund_symbol,
    vault_balance,
    wallet_isConnected,
    tokens_in_focus,
    vault_price_per_token,
    fund_usdc_value,
    sell_usdc_value,
    returned_fund_tokens,
    returned_usdc_tokens,
    is_loading,
    form_valid,
    paused,
    error_reason,
  } = useVaultRedeemFields({ symbol })

  const redeem_query = useRedeemVault({ symbol })

  const { submitted, onSubmit, modal } = useFlowControl({
    steps,
    extra_args: {},
    final_action: () =>
      redeem_query.mutateAsync(form.getValues()).then(onCompleted),
  })

  return (
    <Stack maw="100%">
      {modal}
      <form onSubmit={form.onSubmit(onSubmit)}>
        <Stack
          maw="calc(100vw - 2 * var(--app-body-margin))"
          w="500px"
          gap="xxs"
        >
          <Box pos="relative">
            <Box className={classes.down_box}>
              <Center h="100%">
                <IconArrowDown
                  style={{ width: '70%', height: '70%' }}
                  stroke={1.5}
                />
              </Center>
            </Box>
            <ContentBox
              p="lg"
              gap="xs"
              ref={tokens_in_focus.ref}
              mod={{
                focused: tokens_in_focus.focused,
                error: Boolean(form.getInputProps('tokens_in').error),
              }}
              data-testid="redeem_container"
            >
              <Text size="md" lh="1" opacity={0.7}>
                Redeem
              </Text>
              <NumberRow
                {...form.getInputProps('tokens_in', { withError: false })}
                disabled={is_loading || submitted}
                aria-label="redeem tokens"
                symbol={symbol}
                balance_number={vault_balance}
                value_number={sell_usdc_value}
                max_button={() =>
                  form.setFieldValue('tokens_in', vault_balance)
                }
              />
            </ContentBox>
          </Box>
          <ContentBox p="lg" gap="xs" data-testid="receive_container">
            <Text size="md" lh="1" opacity={0.7}>
              Receive
            </Text>
            <NumberRow
              readonly
              aria-label={`${fund_symbol} tokens`}
              value={returned_fund_tokens}
              symbol={fund_symbol}
              value_number={fund_usdc_value}
            />
            <NumberRow
              readonly
              aria-label="USDC tokens"
              value={returned_usdc_tokens}
              symbol="USDC"
            />
          </ContentBox>
          <FlowButton
            wallet_isConnected={wallet_isConnected}
            form_valid={form_valid}
            is_loading={is_loading}
            paused={paused}
            error_reason={error_reason}
            mutation_query={redeem_query}
            action_word="Redeem"
          />
          <RatioAndDisclosures
            symbol={fund_symbol}
            price={vault_price_per_token}
            maximumFractionDigits={6}
          >
            <Stack gap="xs" px="sm">
              <Group justify="space-between">
                <Text size="sm">TODO</Text>
              </Group>
            </Stack>
          </RatioAndDisclosures>
        </Stack>
      </form>
    </Stack>
  )
})
